import React from 'react';
import {
  Brain, TrendingUp, Target, BarChart3, Zap,
  Activity, AlertTriangle, Clock, Wrench, Stethoscope,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const WindTurbineMonitoringPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Wind Turbine Monitoring',
      title: (
        <>
          Monitor, diagnose, and <em>extend turbine life</em>
        </>
      ),
      lede: 'Monitor and assess wind asset portfolios. Identify root-causes for rapid technical resolution.',
      image: '/assets/img/general/wind-power-generation-8370867_1280.jpg',
      imageAlt: 'Wind turbines in operation',
      actions: [
        { label: 'Talk to us', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '25%', label: 'Energy increase' },
        { value: '35%', label: 'Cost reduction' },
        { value: '99.5%', label: 'Accuracy rate' },
        { value: '24/7', label: 'Monitoring' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Live monitoring with automatic root-cause diagnostics',
      lede: 'Proactively detect, diagnose, and resolve issues before they turn into downtime.',
      body: [
        'Our live monitoring platform empowers wind farm operators with asset health analytics, smart alerts, and automatic root-cause diagnostics.',
      ],
      checklist: [
        'Increase energy generation by 15-25%',
        'Reduce maintenance costs by 35%',
        'Improve turbine efficiency by 20%',
        'Minimize downtime through predictive maintenance',
      ],
      image: '/assets/img/general/windmills-1838788_1280.jpg',
      imageAlt: 'Wind farm under continuous monitoring',
      imageCaption: 'Asset health analytics and smart alerts across the whole wind portfolio',
    }}
    capabilities={{
      eyebrow: 'Platform features',
      title: 'Diagnostics that reach the root cause',
      lede: 'Analysis built specifically around wind asset behaviour.',
      items: [
        {
          icon: Brain,
          title: 'AI-powered analysis',
          description: 'Advanced machine learning models analyze wind patterns and turbine performance data.',
        },
        {
          icon: Target,
          title: 'Root cause identification',
          description: 'Intelligent algorithms identify the root causes of energy losses and inefficiencies.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to bridge the gap between target and actual generation.',
        },
        {
          icon: BarChart3,
          title: 'Predictive analytics',
          description: 'Forecast potential issues and optimize energy output using historical data.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'AI capabilities',
      title: 'Purpose-built models and engines',
      lede: 'Engineered to keep turbines at peak output.',
      items: [
        {
          icon: Brain,
          title: 'Machine learning models',
          description: 'Pre-trained models specifically designed for wind energy optimization.',
        },
        {
          icon: Zap,
          title: 'Real-time processing',
          description: 'Instant analysis of turbine data for immediate optimization.',
        },
        {
          icon: Activity,
          title: 'Pattern recognition',
          description: 'Identify complex patterns in wind behavior and turbine performance.',
        },
        {
          icon: AlertTriangle,
          title: 'Anomaly detection',
          description: 'Early detection of potential issues before they impact performance.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Real-world outcomes',
      title: 'What operators see in the field',
      items: [
        {
          icon: Clock,
          title: 'Reduced downtime',
          description: 'Instant alerts enable faster response and minimize generation losses.',
        },
        {
          icon: Wrench,
          title: 'Improved turbine longevity',
          description: 'Catch anomalies early and schedule predictive maintenance.',
        },
        {
          icon: Stethoscope,
          title: 'Actionable diagnostics',
          description: 'Automated root-cause analysis guides technicians to quick, effective solutions.',
        },
      ],
    }}
    cta={{
      title: 'See your turbines in a new light',
      body: 'Request a walkthrough of our live monitoring and diagnostics.',
      primary: { label: 'Talk to us', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default WindTurbineMonitoringPage;
